/*
 * Purpose: Client subscription state — sync with server user state + mock sessionStorage
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { AUTH_CONFIG } from '@/lib/auth-config';
import { patchUserState, type UserStateBlob } from '@/lib/user-state-api';
import type { SubscriptionTier } from '@/lib/subscriptionPolicy';

const MOCK_KEYS = {
  guest: 'auth:guest-mode',
  tier: 'auth:subscription-tier',
  status: 'auth:subscription-status',
} as const;

export type PlanSelectionPatch = {
  subscriptionTier?: SubscriptionTier | null;
  subscriptionStatus?: 'active' | 'guest' | 'none';
  guestMode?: boolean;
  planSelectedAt?: string;
};

function readMockPlanState(): UserStateBlob {
  if (typeof window === 'undefined') return {};
  const guestMode = window.sessionStorage.getItem(MOCK_KEYS.guest) === '1';
  const tier = window.sessionStorage.getItem(MOCK_KEYS.tier);
  return {
    guestMode,
    subscriptionTier: tier === 'basic' || tier === 'plus' ? tier : undefined,
    subscriptionStatus: guestMode ? 'guest' : tier ? 'active' : 'none',
  };
}

function writeMockPlanState(patch: PlanSelectionPatch): UserStateBlob {
  if (typeof window === 'undefined') return patch;
  if (patch.guestMode === true) {
    window.sessionStorage.setItem(MOCK_KEYS.guest, '1');
    window.sessionStorage.removeItem(MOCK_KEYS.tier);
    window.sessionStorage.setItem(MOCK_KEYS.status, 'guest');
  } else if (patch.subscriptionTier) {
    window.sessionStorage.removeItem(MOCK_KEYS.guest);
    window.sessionStorage.setItem(MOCK_KEYS.tier, patch.subscriptionTier);
    window.sessionStorage.setItem(MOCK_KEYS.status, 'active');
  }
  return readMockPlanState();
}

export async function applyPlanSelection(
  patch: PlanSelectionPatch
): Promise<UserStateBlob | null> {
  const payload: UserStateBlob = {
    ...patch,
    planSelectedAt: patch.planSelectedAt ?? new Date().toISOString(),
  };
  if (AUTH_CONFIG.mock) {
    return writeMockPlanState(patch);
  }
  return patchUserState(payload);
}

export function mergeSubscriptionState(
  serverState: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (AUTH_CONFIG.mock) {
    return { ...readMockPlanState(), ...serverState };
  }
  return serverState ?? {};
}
